import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Activity, ArrowRight, Bell, BellRing, BookOpen, Briefcase, Check, CheckCircle2, ChevronLeft, Eye, Gavel, Loader2, Plus, Trash2, Users } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const WATCH_TYPES = [
  { value: "judge", label: "Judge", icon: Gavel, desc: "Monitor judge's new opinions" },
  { value: "party", label: "Party/Entity", icon: Users, desc: "Track litigation involving a party" },
  { value: "attorney", label: "Attorney", icon: Briefcase, desc: "Follow attorney's cases" },
  { value: "keyword", label: "Legal Issue", icon: BookOpen, desc: "Monitor cases on specific topics" }
];

const WatchlistPage = () => {
  const [watchlist, setWatchlist] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newItem, setNewItem] = useState({ watch_type: "judge", name: "", jurisdiction: "", notes: "" });
  
  const fetchData = async () => {
    try {
      const [watchlistRes, alertsRes] = await Promise.all([
        axios.get(`${API}/watchlist`),
        axios.get(`${API}/alerts?limit=20`)
      ]);
      setWatchlist(watchlistRes.data.items || []);
      setAlerts(alertsRes.data.alerts || []);
      setUnreadCount(alertsRes.data.unread_count || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const addToWatchlist = async () => {
    if (!newItem.name.trim()) return;
    try {
      await axios.post(`${API}/watchlist`, newItem);
      setNewItem({ watch_type: "judge", name: "", jurisdiction: "", notes: "" });
      setShowAdd(false);
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };
  
  const removeFromWatchlist = async (id) => {
    if (!window.confirm("Remove from watchlist?")) return;
    await axios.delete(`${API}/watchlist/${id}`);
    fetchData();
  };
  
  const checkForUpdates = async () => {
    setChecking(true);
    try {
      const res = await axios.post(`${API}/watchlist/check`);
      if (res.data.alerts_generated > 0) {
        fetchData();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setChecking(false);
    }
  };
  
  const markAsRead = async (alertId) => {
    await axios.put(`${API}/alerts/${alertId}/read`);
    setAlerts(alerts.map(a => a.id === alertId ? { ...a, is_read: true } : a));
    setUnreadCount(Math.max(0, unreadCount - 1));
  };
  
  const markAllRead = async () => {
    await axios.put(`${API}/alerts/read-all`);
    setAlerts(alerts.map(a => ({ ...a, is_read: true })));
    setUnreadCount(0);
  };
  
  const getWatchTypeIcon = (type) => {
    const t = WATCH_TYPES.find(w => w.value === type);
    return t ? t.icon : Eye;
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="watchlist-page">
      <Disclaimer />
      <div className="container-shell py-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
              <ChevronLeft className="h-4 w-4" /> Back to Tools
            </Link>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Litigation Watchlist</h1>
              {unreadCount > 0 && (
                <Badge className="bg-red-500">{unreadCount} new</Badge>
              )}
            </div>
            <p className="text-muted-foreground">Monitor judges, parties, attorneys, and legal issues for new filings</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={checkForUpdates} disabled={checking}>
              {checking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Activity className="h-4 w-4 mr-2" />}
              Check Now
            </Button>
            <Button onClick={() => setShowAdd(true)}>
              <Plus className="h-4 w-4 mr-2" /> Add to Watchlist
            </Button>
          </div>
        </div>
        
        {/* Add Modal */}
        {showAdd && (
          <Card className="mb-6 border-primary animate-fade-in">
            <CardHeader>
              <CardTitle>Add to Watchlist</CardTitle>
              <CardDescription>Choose what you want to monitor</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 md:grid-cols-4">
                {WATCH_TYPES.map(type => (
                  <button
                    key={type.value}
                    onClick={() => setNewItem(p => ({ ...p, watch_type: type.value }))}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      newItem.watch_type === type.value 
                        ? 'border-primary bg-primary/5' 
                        : 'hover:border-slate-300'
                    }`}
                  >
                    <type.icon className={`h-5 w-5 mb-2 ${newItem.watch_type === type.value ? 'text-primary' : 'text-muted-foreground'}`} />
                    <h4 className="font-medium">{type.label}</h4>
                    <p className="text-xs text-muted-foreground">{type.desc}</p>
                  </button>
                ))}
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Name to Monitor</Label>
                  <Input 
                    value={newItem.name}
                    onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))}
                    placeholder={
                      newItem.watch_type === "judge" ? "e.g., Posner" :
                      newItem.watch_type === "party" ? "e.g., State Farm Insurance" :
                      newItem.watch_type === "attorney" ? "e.g., Smith" :
                      "e.g., summary judgment standard"
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Jurisdiction (Optional)</Label>
                  <Select value={newItem.jurisdiction} onValueChange={v => setNewItem(p => ({ ...p, jurisdiction: v }))}>
                    <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Federal</SelectItem>
                      <SelectItem value="ca9">9th Circuit</SelectItem>
                      <SelectItem value="ca2">2nd Circuit</SelectItem>
                      <SelectItem value="ca5">5th Circuit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-1.5">
                <Label>Notes (Optional)</Label>
                <Input 
                  value={newItem.notes}
                  onChange={e => setNewItem(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Why are you monitoring this?"
                />
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
                <Button onClick={addToWatchlist}>Add to Watchlist</Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Watchlist Items */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5" /> Monitoring
                </CardTitle>
                <CardDescription>{watchlist.length} items</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : watchlist.length === 0 ? (
                  <div className="text-center py-8">
                    <Eye className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">No items in watchlist</p>
                    <Button variant="outline" className="mt-3" onClick={() => setShowAdd(true)}>
                      Add First Item
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {watchlist.map(item => {
                      const Icon = getWatchTypeIcon(item.watch_type);
                      return (
                        <div key={item.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Icon className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <h4 className="font-medium text-sm">{item.name}</h4>
                              <p className="text-xs text-muted-foreground capitalize">{item.watch_type}</p>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => removeFromWatchlist(item.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Alerts */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <BellRing className="h-5 w-5" /> Alerts
                    </CardTitle>
                    <CardDescription>
                      {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                    </CardDescription>
                  </div>
                  {unreadCount > 0 && (
                    <Button variant="outline" size="sm" onClick={markAllRead}>
                      Mark All Read
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {alerts.length === 0 ? (
                  <div className="text-center py-8">
                    <Bell className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">No alerts yet</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Click "Check Now" to scan for new filings
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {alerts.map(alert => {
                      const Icon = getWatchTypeIcon(alert.watch_type);
                      return (
                        <div 
                          key={alert.id} 
                          className={`p-4 rounded-xl border transition-colors ${
                            alert.is_read ? 'bg-slate-50' : 'bg-orange-50 border-orange-200'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground capitalize">{alert.watch_type}: {alert.watch_name}</span>
                            </div>
                            {!alert.is_read && (
                              <Button variant="ghost" size="sm" onClick={() => markAsRead(alert.id)}>
                                <CheckCircle2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          <h4 className="font-semibold mb-1">{alert.title}</h4>
                          {alert.court && (
                            <p className="text-xs text-muted-foreground mb-2">{alert.court} • {alert.date_filed}</p>
                          )}
                          {alert.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2">{alert.description}</p>
                          )}
                          {alert.url && (
                            <a 
                              href={alert.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-primary hover:underline flex items-center gap-1 mt-2"
                            >
                              View Case <ArrowRight className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
};


export default WatchlistPage;
